import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, startOfWeek, addDays, isSameDay, startOfDay } from 'date-fns';
import { de } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import { useAuth } from '../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import { productService } from '../services/productService';
import MealModal from '../components/MealModal';
import ShoppingListSelectorModal from '../components/ShoppingListSelectorModal';

const { 
  FiPlus, FiShoppingBag, FiTrash2, FiEdit2, 
  FiChevronLeft, FiChevronRight, FiStar, FiSearch, FiX,
  FiList, FiMove
} = FiIcons;

const MEAL_TYPES = ['Frühstück', 'Mittagessen', 'Abendessen', 'Snack'];

export default function MealPlanning() {
  const { state, dispatch } = useFamilyContext();
  const { mealPlans, shoppingLists } = state;
  const { user, familyId } = useAuth();

  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [showModal, setShowModal] = useState(false);
  const [showFavoritesSidebar, setShowFavoritesSidebar] = useState(true);
  const [showShoppingSelector, setShowShoppingSelector] = useState(false);
  const [editingMeal, setEditingMeal] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedIngredients, setSelectedIngredients] = useState('');
  const [searchFavorite, setSearchFavorite] = useState('');

  const weekDays = Array.from({ length: 7 }).map((_, i) => addDays(currentWeekStart, i));

  // Filtered unique favorites for the sidebar
  const uniqueFavorites = useMemo(() => {
    const favs = mealPlans.filter(m => m.isFavorite || m.is_favorite);
    const seen = new Set();
    return favs.filter(m => {
      const key = m.dish.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).filter(m => m.dish.toLowerCase().includes(searchFavorite.toLowerCase()));
  }, [mealPlans, searchFavorite]);

  const handleSaveMeal = (formData) => {
    if (editingMeal && editingMeal.id && !editingMeal.isDrag) {
      dispatch({ 
        type: 'UPDATE_MEAL_PLAN', 
        payload: { ...formData, id: editingMeal.id } 
      });
    } else {
      dispatch({ 
        type: 'ADD_MEAL_PLAN', 
        payload: { ...formData, id: uuidv4() } 
      });
    }
    setShowModal(false);
  };

  const openModal = (date, meal = null) => {
    setSelectedDate(date);
    setEditingMeal(meal);
    setShowModal(true);
  };

  const handleAddToShoppingList = (ingredients) => {
    if (!ingredients) return;
    setSelectedIngredients(ingredients);
    setShowShoppingSelector(true);
  };

  const confirmAddToShoppingList = async (listId, newListName) => {
    let targetListId = listId;

    if (listId === 'new') {
      const newListId = uuidv4();
      await dispatch({
        type: 'ADD_SHOPPING_LIST',
        payload: {
          id: newListId,
          name: newListName || 'Neue Liste',
          family_id: familyId,
          created_by: user.id
        }
      });
      targetListId = newListId;
    }

    const items = selectedIngredients.split(',').map(i => i.trim()).filter(Boolean);
    
    for (const item of items) {
      const cat = await productService.categorize(item);
      dispatch({
        type: 'ADD_SHOPPING_ITEM',
        payload: {
          id: uuidv4(),
          list_id: targetListId,
          listId: targetListId,
          name: item,
          category: cat || 'Sonstiges',
          completed: false,
          added_by: user.id,
          family_id: familyId
        }
      });
    }

    setShowShoppingSelector(false);
  };

  // Drag & Drop Handlers
  const onDragStart = (e, meal) => {
    e.dataTransfer.setData('mealData', JSON.stringify(meal));
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const onDrop = (e, date, mealType) => {
    e.preventDefault();
    try {
      const mealData = JSON.parse(e.dataTransfer.getData('mealData'));
      const newMeal = {
        ...mealData,
        id: uuidv4(),
        date: date.toISOString(),
        mealType: mealType,
        meal_type: mealType
      };
      
      // Check if a meal already exists in this slot
      const existing = mealPlans.find(m => 
        isSameDay(new Date(m.date), date) && 
        (m.mealType === mealType || m.meal_type === mealType)
      );

      if (existing) {
        dispatch({ type: 'DELETE_MEAL_PLAN', payload: existing.id });
      }

      dispatch({ type: 'ADD_MEAL_PLAN', payload: newMeal });
    } catch (err) {
      console.error('Drop failed:', err);
    }
  };

  return (
    <div className="flex bg-gray-50 dark:bg-gray-900 -m-6 min-h-[calc(100vh-4rem)] lg:min-h-screen overflow-hidden">
      {/* Sidebar: Favorites */}
      <motion.aside 
        initial={false}
        animate={{ width: showFavoritesSidebar ? 320 : 0, opacity: showFavoritesSidebar ? 1 : 0 }}
        className="hidden md:flex flex-col bg-white dark:bg-gray-800 border-r dark:border-gray-700 shadow-sm overflow-hidden"
      >
        <div className="p-6 border-b dark:border-gray-700 flex items-center justify-between">
          <h2 className="font-black text-xl flex items-center dark:text-white">
            <SafeIcon icon={FiStar} className="w-5 h-5 mr-2 text-yellow-500 fill-current" />
            Favoriten
          </h2>
          <button onClick={() => setShowFavoritesSidebar(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <SafeIcon icon={FiChevronLeft} className="w-5 h-5 dark:text-white" />
          </button>
        </div>
        
        <div className="p-4 border-b dark:border-gray-700">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Suchen..."
              value={searchFavorite}
              onChange={(e) => setSearchFavorite(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {uniqueFavorites.length > 0 ? (
            uniqueFavorites.map((fav) => (
              <div
                key={fav.id}
                draggable
                onDragStart={(e) => onDragStart(e, fav)}
                className="p-4 bg-white dark:bg-gray-700 rounded-2xl border border-gray-200 dark:border-gray-600 shadow-sm cursor-grab active:cursor-grabbing hover:border-blue-400 dark:hover:border-blue-500 transition-all group"
              >
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-blue-600 truncate mr-2">{fav.dish}</h4>
                  <SafeIcon icon={FiMove} className="w-4 h-4 text-gray-300 group-hover:text-blue-400" />
                </div>
                {fav.ingredients && (
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-1 italic">{fav.ingredients}</p>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-10">
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-full inline-block mb-3">
                <SafeIcon icon={FiStar} className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 px-6">
                Keine Favoriten gefunden. Markiere Mahlzeiten mit einem Stern!
              </p>
            </div>
          )}
        </div>
        
        <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-t dark:border-gray-700">
           <p className="text-[10px] text-blue-600 dark:text-blue-400 font-bold uppercase text-center">Tipp: Drag & Drop zum Planen</p>
        </div>
      </motion.aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto p-6 md:p-8">
        <div className="max-w-[1600px] mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-black text-gray-900 dark:text-white">Menüplanung</h1>
              <p className="text-gray-500 dark:text-gray-400 font-medium">Was gibt es diese Woche zu essen?</p>
            </div>
            <div className="flex items-center space-x-3">
              {!showFavoritesSidebar && (
                <button 
                  onClick={() => setShowFavoritesSidebar(true)}
                  className="hidden md:flex items-center px-4 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-white rounded-2xl shadow-sm border dark:border-gray-700 hover:bg-gray-50 transition-all font-bold"
                >
                  <SafeIcon icon={FiStar} className="w-5 h-5 mr-2 text-yellow-500" />
                  Favoriten zeigen
                </button>
              )}
              <div className="flex items-center space-x-1 bg-white dark:bg-gray-800 p-1.5 rounded-2xl shadow-md border dark:border-gray-700">
                <button 
                  onClick={() => setCurrentWeekStart(addDays(currentWeekStart, -7))}
                  className="p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                >
                  <SafeIcon icon={FiChevronLeft} className="w-6 h-6 dark:text-white" />
                </button>
                <div className="px-6 text-sm font-black dark:text-white min-w-[120px] text-center">
                  KW {format(currentWeekStart, 'w')} • {format(currentWeekStart, 'yyyy')}
                </div>
                <button 
                  onClick={() => setCurrentWeekStart(addDays(currentWeekStart, 7))}
                  className="p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                >
                  <SafeIcon icon={FiChevronRight} className="w-6 h-6 dark:text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Week Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-6">
            {weekDays.map((day) => {
              const dayLevelMeals = mealPlans.filter(m => isSameDay(new Date(m.date), day));
              const isToday = isSameDay(day, new Date());

              return (
                <div 
                  key={day.toISOString()} 
                  className={`bg-white dark:bg-gray-800 rounded-3xl shadow-sm border-2 transition-all flex flex-col min-h-[500px] ${
                    isToday ? 'border-blue-500 ring-4 ring-blue-500/10' : 'border-gray-100 dark:border-gray-700'
                  }`}
                >
                  <div className={`p-5 border-b-2 dark:border-gray-700 text-center rounded-t-[22px] ${isToday ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                    <p className={`text-xs font-black uppercase tracking-widest mb-1 ${isToday ? 'text-blue-600' : 'text-gray-400'}`}>
                      {format(day, 'EEEE', { locale: de })}
                    </p>
                    <p className={`text-xl font-black ${isToday ? 'text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                      {format(day, 'dd.MM.')}
                    </p>
                  </div>

                  <div className="flex-1 p-4 space-y-6">
                    {MEAL_TYPES.map(type => {
                      const meal = dayLevelMeals.find(m => m.mealType === type || m.meal_type === type);
                      
                      return (
                        <div 
                          key={type} 
                          className="space-y-2"
                          onDragOver={onDragOver}
                          onDrop={(e) => onDrop(e, day, type)}
                        >
                          <div className="flex items-center justify-between px-1">
                            <p className="text-[11px] uppercase font-black text-gray-400 tracking-wider flex items-center">
                              <span className="w-1.5 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full mr-2"></span>
                              {type}
                            </p>
                          </div>

                          {meal ? (
                            <motion.div 
                              layoutId={meal.id}
                              className="bg-gray-50 dark:bg-gray-700 group relative p-4 rounded-2xl border-2 border-transparent hover:border-blue-200 dark:hover:border-blue-900 transition-all shadow-sm"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="text-sm font-black text-gray-900 dark:text-white leading-tight pr-4">
                                  {meal.dish}
                                </h4>
                                {(meal.isFavorite || meal.is_favorite) && (
                                  <SafeIcon icon={FiStar} className="w-3.5 h-3.5 text-yellow-500 fill-current flex-shrink-0" />
                                )}
                              </div>
                              
                              {meal.ingredients && (
                                 <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-3 line-clamp-2 italic leading-relaxed">
                                   {meal.ingredients}
                                 </p>
                              )}

                              <div className="flex items-center justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-all">
                                 <button 
                                   onClick={() => handleAddToShoppingList(meal.ingredients)}
                                   className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-xl transition-all"
                                   title="Einkaufen"
                                 >
                                   <SafeIcon icon={FiShoppingBag} className="w-4 h-4" />
                                 </button>
                                 <button 
                                   onClick={() => openModal(day, meal)}
                                   className="p-2 text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded-xl transition-all"
                                 >
                                   <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                                 </button>
                                 <button 
                                   onClick={() => dispatch({ type: 'DELETE_MEAL_PLAN', payload: meal.id })}
                                   className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-all"
                                 >
                                   <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                 </button>
                              </div>
                            </motion.div>
                          ) : (
                            <button 
                              onClick={() => openModal(day, { mealType: type, meal_type: type })}
                              className="w-full py-6 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl text-gray-300 hover:text-blue-400 hover:border-blue-300 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all group flex flex-col items-center justify-center gap-2"
                            >
                              <div className="p-2 rounded-full bg-gray-50 dark:bg-gray-800 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20">
                                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                              </div>
                              <span className="text-[10px] font-black uppercase tracking-widest">Planen</span>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      {/* Modals */}
      <AnimatePresence>
        {showModal && (
          <MealModal 
            date={selectedDate} 
            meal={editingMeal} 
            onClose={() => setShowModal(false)} 
            onSave={handleSaveMeal} 
          />
        )}
        {showShoppingSelector && (
          <ShoppingListSelectorModal 
            ingredients={selectedIngredients} 
            onClose={() => setShowShoppingSelector(false)} 
            onConfirm={confirmAddToShoppingList} 
          />
        )}
      </AnimatePresence>
    </div>
  );
}